// ─────────────────────────────────────────────
//  taskRecipients — who the Task Directives card may send to, and what it says
//  about the ones it still offers.
//
//  ⚠️ PURE AND HERE, NOT INLINE IN THE PAGE. It lived as an IIFE inside
//  TimelineChartPage, which has no test file, so the rule was invisible to the
//  suite — the same reason `orienteeBoardRows` was lifted out of a `useMemo` in
//  that same page. A rule about who can be sent a clinical instruction is not a
//  rule that should be untestable.
//
//  ⚠️ THREE NOTES, AND ONLY ONE OF THEM MEANS GONE.
//    • "left for the day"     — departed. REMOVED from the list entirely.
//    • "sent home from call"  — released from call; may still be in the building.
//    • "leaves 17:00"         — here right now, going later.
//  The last two are shown and marked, because they can still be reached.
//
//  ⚠️ THIS REVERSES THE CARD'S OWN DOCTRINE, DELIBERATELY. TimelineTaskCard says
//  "MARKED, NEVER HIDDEN … a name missing from the roster reads as a bug, and a
//  departed clinician can genuinely be called back." The owner asked on
//  2026-09-10 for departed people off this list, having been shown exactly that
//  cost: the board can no longer be used to call somebody back in. Recorded here
//  rather than quietly reversed, so the next reader knows it was a decision.
// ─────────────────────────────────────────────

/**
 * @param pools    { doctors, crnas } — today's roster pools, already enriched
 * @param roster   the day's roster doc (departed, locumLeaveTimes, mdCallHome)
 * @returns the people the card may offer, each with an `absenceNote` when there
 *          is something true to say about them.
 */
export function taskRecipients({ doctors = [], crnas = [] } = {}, roster = null) {
  const departed = roster?.departed || {};
  const locumLeave = roster?.locumLeaveTimes || {};
  const homeCodes = new Set(roster?.mdCallHome || []);

  return [...(Array.isArray(doctors) ? doctors : []), ...(Array.isArray(crnas) ? crnas : [])]
    .filter((p) => p && p.id && !departed[p.id])
    .map((p) => {
      const leaveAt = locumLeave[p.shortCode] || locumLeave[p.id] || "";
      const calledHome = p.shortCode ? homeCodes.has(p.shortCode) : false;
      // No "left for the day" branch: those people are filtered out above, so a
      // branch for them here would be dead code pretending to be a safety net.
      const note = calledHome
        ? "sent home from call"
        : leaveAt
          ? `leaves ${leaveAt}`
          : "";
      return note ? { ...p, absenceNote: note } : p;
    });
}
